// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { askEquipmentQuestion } from '../../services/equipmentDocsService';
import type { EquipmentDocsResponse, EquipmentType } from '../../types';
import SopFactoryTable from '../../components/sop/SopFactoryTable';
import './EquipmentDocs.css';

// 'all' is a UI-only value that maps to equipment_type: null (search all manuals).
type TypeFilter = EquipmentType | 'all';

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'chiller', label: 'Chiller' },
  { value: 'ups', label: 'UPS' },
  { value: 'generator', label: 'Generator' },
  { value: 'all', label: 'All equipment' },
];

const TYPE_LABELS: Record<string, string> = {
  chiller: 'chiller',
  ups: 'UPS',
  generator: 'generator',
};

function describeFilter(equipmentType: EquipmentType | null): string {
  return equipmentType ? (TYPE_LABELS[equipmentType] ?? equipmentType) : 'all equipment';
}

function generateRequestId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function EquipmentDocs() {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('chiller');
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState<EquipmentDocsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showRaw, setShowRaw] = useState(false);

  const handleSubmit = useCallback(async () => {
    const trimmed = question.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const result = await askEquipmentQuestion({
        question: trimmed,
        equipment_type: typeFilter === 'all' ? null : typeFilter,
        request_id: generateRequestId(),
      });
      setResponse(result);
    } catch (err) {
      setError((err as Error).message || 'Failed to get an answer.');
    } finally {
      setLoading(false);
    }
  }, [question, typeFilter, loading]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Submit on Cmd/Ctrl+Enter, allow newlines otherwise.
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const showEmptyState = response?.no_answer === true;
  const showAnswer = response !== null && !response.no_answer;

  return (
    <div className="equipment-docs">
      <div className="equipment-docs-header">
        <h2 className="equipment-docs-title">Equipment Documentation</h2>
        <p className="equipment-docs-subtitle">
          Ask a question about a piece of equipment and get an answer grounded in the manuals, with
          source citations.
        </p>
      </div>

      <div className="equipment-docs-form">
        <div className="equipment-docs-field equipment-docs-field-type">
          <label htmlFor="equipment-type">Equipment type</label>
          <select
            id="equipment-type"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value as TypeFilter)}
            disabled={loading}
          >
            {TYPE_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="equipment-docs-field equipment-docs-field-question">
          <label htmlFor="equipment-question">Question</label>
          <textarea
            id="equipment-question"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. What is the leaving chilled liquid setpoint range?"
            rows={3}
            disabled={loading}
          />
        </div>

        <div className="equipment-docs-actions">
          <button
            className="equipment-docs-submit"
            onClick={handleSubmit}
            disabled={loading || !question.trim()}
          >
            {loading ? 'Asking…' : 'Ask'}
          </button>
        </div>
      </div>

      {error && <div className="equipment-docs-error">{error}</div>}

      {response && (
        <p className="equipment-docs-answering-about">
          Answering about: <strong>{describeFilter(response.equipment_type)}</strong>
        </p>
      )}

      {showEmptyState && (
        <div className="equipment-docs-empty">
          No grounded answer was found in the manuals for this question.
        </div>
      )}

      {showAnswer && (
        <div className="equipment-docs-answer-card">
          <h3 className="equipment-docs-section-title">Answer</h3>
          <div className="equipment-docs-answer">
            <ReactMarkdown
              components={{
                a: ({ href, children, ...props }) => (
                  <a {...props} href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
              }}
            >
              {response.answer}
            </ReactMarkdown>
          </div>

          {response.citations.length > 0 && (
            <div className="equipment-docs-citations">
              <h4 className="equipment-docs-citations-title">Sources</h4>
              <div className="equipment-docs-chips">
                {response.citations.map((c, i) => {
                  const label = c.page ? `${c.source_doc} · p.${c.page}` : c.source_doc;
                  return c.url ? (
                    <a
                      key={`${c.source_doc}-${i}`}
                      className="equipment-docs-chip equipment-docs-chip-link"
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={c.snippet ?? undefined}
                    >
                      {label}
                    </a>
                  ) : (
                    <span
                      key={`${c.source_doc}-${i}`}
                      className="equipment-docs-chip"
                      title={c.snippet ?? undefined}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {response && (
        <div className="equipment-docs-raw-section">
          <button
            className="equipment-docs-raw-toggle"
            onClick={() => setShowRaw(!showRaw)}
            aria-expanded={showRaw}
          >
            <span className={`equipment-docs-toggle-arrow ${showRaw ? 'open' : ''}`}>&#9654;</span>
            Raw JSON
          </button>
          {showRaw && (
            <pre className="equipment-docs-raw-content">{JSON.stringify(response, null, 2)}</pre>
          )}
        </div>
      )}

      <div className="equipment-docs-sops">
        <h2 className="equipment-docs-sops-title">Standard Operating Procedures</h2>
        <SopFactoryTable />
      </div>
    </div>
  );
}

export default EquipmentDocs;
